/**
 * The living clearing — the sea-monster surfacing cycle from Map Update 2.
 *
 * Shipped by the map build as a bare IIFE bound to document ids. It is
 * adapted here for two facts about how the storefront renders the chart:
 *
 *  1. The artwork is CLONED into stacked layers, so ids are not unique —
 *     every lookup is scoped to the layer it is animating.
 *  2. Only the top layer is visible. Animating the copy underneath is work
 *     for pixels nobody sees, so this runs against one layer at a time and
 *     is re-pointed when the layers swap.
 *
 * Returns a handle with stop(), so the caller can move it or shut it down.
 */
window.couorStartMapLife = function (root, phase0) {
  // phase0 lets a caller resume the 17s cycle where a previous run left
  // off -- the map pauses this during camera flights, and restarting from
  // zero would snap a surfaced monster underwater on arrival.
  const handle = { raf: 0, stop() { if (handle.raf) cancelAnimationFrame(handle.raf); handle.raf = 0; } };

  var NS='http://www.w3.org/2000/svg';
  var rise=root.querySelector('#cm-smrise'),calm=root.querySelector('#cm-smcalm'),
      churn=root.querySelector('#cm-smchurn'),spout=root.querySelector('#cm-smspout'),
      froth=root.querySelector('#cm-smfroth');
  if(!rise)return handle;
  // Timeline (14s): 0-5 calm | 5-8 churn | 8-8.8 rise | 8.8-12.8 surfaced (spout 9.2-12.2) | 12.8-13.6 sink | fade calm back
  var NOZ=[[-2.7,0.7],[3.5,-0.1]],G=42,SEA=11.5;
  // Each run owns its particle pool. A previous run's pool is dead weight —
  // and if that run was stopped mid-spout, its drops are frozen VISIBLE in
  // the air with nothing left managing them. Sweep them out before
  // building this run's pool (also stops the node count growing by 124
  // per camera flight).
  Array.prototype.slice.call(spout.querySelectorAll('[data-couor-fx]'))
    .forEach(function(n){n.parentNode&&n.parentNode.removeChild(n);});
  var drops=[],splashes=[];
  for(var i=0;i<110;i++){var l=document.createElementNS(NS,'line');
    l.setAttribute('stroke',i%3?'#7d9299':'#33484e');
    l.setAttribute('stroke-linecap','round');l.setAttribute('visibility','hidden');
    l.setAttribute('data-couor-fx','1');
    spout.appendChild(l);drops.push({el:l,live:false});}
  for(var i2=0;i2<14;i2++){var el=document.createElementNS(NS,'ellipse');
    el.setAttribute('fill','none');el.setAttribute('stroke','#33484e');
    el.setAttribute('visibility','hidden');el.setAttribute('data-couor-fx','1');
    spout.appendChild(el);splashes.push({el:el,live:false});}
  // Pausing may land mid-spout. The monster's body may freeze where it is
  // (consistent, clipped by the sea), but airborne water frozen in the sky
  // reads as a glitch — douse every live particle on the way out.
  handle.stop=function(){
    if(handle.raf)cancelAnimationFrame(handle.raf);handle.raf=0;
    drops.forEach(function(d){if(d.live){d.live=false;d.el.setAttribute('visibility','hidden');}});
    splashes.forEach(function(s){if(s.live){s.live=false;s.el.setAttribute('visibility','hidden');}});
  };
  var seat=root.querySelector('#cm-smseat');
  var rips=Array.prototype.slice.call(churn.querySelectorAll('ellipse'));
  var t0=performance.now()-((phase0||0)*1000),last=performance.now(),acc=0;
  handle.phase=function(){return ((performance.now()-t0)/1000)%17;};
  function ease(k){return k<0?0:k>1?1:k*k*(3-2*k);}
  function splash(x){
    for(var i=0;i<splashes.length;i++){var sp=splashes[i];if(sp.live)continue;
      sp.x=x;sp.age=0;sp.live=true;
      sp.el.setAttribute('cx',x);sp.el.setAttribute('cy',SEA+0.6+Math.random());
      sp.el.setAttribute('visibility','visible');return;}}
  // Every attribute write below invalidates the layer -- style, paint,
  // raster. On a phone, doing that at the display's full 60-120Hz eats
  // frame budget the camera needs; at 30Hz the same slow, organic motion
  // is indistinguishable and the paint cost halves (or better). Pointer
  // coarseness is the phone signal; fine pointers keep the full rate.
  var TICK=window.matchMedia&&window.matchMedia('(pointer: coarse)').matches?33:0;
  var lastTick=0;
  function frame(now){
    if(TICK&&now-lastTick<TICK){handle.raf=requestAnimationFrame(frame);return;}
    lastTick=now;
    var dt=Math.min(0.05,(now-last)/1000);last=now;
    var t=((now-t0)/1000)%17;
    calm.setAttribute('opacity', t<5?'0.85':(t>16.3?((t-16.3)*2*0.85).toFixed(2):'0'));
    var ch=0;
    if(t>=5&&t<8.4)ch=Math.min(1,(t-5)/0.8)*(t>8?Math.max(0,(8.4-t)/0.4):1);
    churn.setAttribute('opacity',ch.toFixed(2));
    if(ch>0){
      rips.forEach(function(rp,i){
        var k=(t*0.9+i*0.33)%1;
        rp.setAttribute('rx',(2+k*11).toFixed(2));
        rp.setAttribute('ry',(0.6+k*3).toFixed(2));
        rp.setAttribute('opacity',(0.55*(1-k)).toFixed(2));
      });
      froth.setAttribute('transform','translate('+(Math.sin(t*21)*0.9).toFixed(2)+','+(Math.sin(t*17)*0.6).toFixed(2)+')');
    }
    var y=46,rot=0;
    if(t>=8&&t<8.8){var k1=ease((t-8)/0.8);y=46-55.5*k1;rot=-38*(1-k1);}
    else if(t>=8.8&&t<15.6){y=-9.5+Math.sin((t-8.8)*1.4)*0.6;rot=Math.sin((t-8.8)*1.1)*1.2;} // gentle bob while surfaced
    else if(t>=15.6&&t<16.4){var k2=ease((t-15.6)/0.8);y=-9.5+55.5*k2;rot=14*k2;}
    rise.setAttribute('transform','translate(0,'+y.toFixed(2)+') rotate('+rot.toFixed(2)+',0,13)');
    var so=0;
    if(t>=8&&t<8.8)so=ease((t-8)/0.8);
    else if(t>=8.8&&t<15.6)so=1;
    else if(t>=15.6&&t<16.4)so=1-ease((t-15.6)/0.8);
    seat.setAttribute('opacity',(so*0.9).toFixed(2));
    if(so>0)seat.setAttribute('transform','translate('+(Math.sin(t*2.2)*0.7).toFixed(2)+','+(Math.sin(t*1.7)*0.5).toFixed(2)+')');
    // spout: pressure ramps up, holds, dies away — a coherent arc, not random spray
    var press=0;
    if(t>=12.8&&t<15.5){
      press=ease((t-12.8)/0.7)*(t>14.7?Math.max(0,(15.5-t)/0.8):1);
      acc+=dt*130*press;
      while(acc>1){acc-=1;
        for(var j=0;j<drops.length;j++){var d0=drops[j];if(d0.live)continue;
          var nz=NOZ[j%2];
          var lane=(j%5)/4-0.5;               // 5 parallel streamlines = engraved hatching
          d0.x=nz[0]+lane*0.9;d0.y=nz[1]+y;
          d0.vx=(4.5+3.5*press)+lane*1.4+(Math.random()-0.5)*0.8;
          d0.vy=-(21+9*press)+lane*1.1+(Math.random()-0.5)*1.2;
          d0.live=true;d0.el.setAttribute('visibility','visible');break;}}
    }
    for(var j2=0;j2<drops.length;j2++){var d=drops[j2];if(!d.live)continue;
      d.vy+=G*dt;d.x+=d.vx*dt;d.y+=d.vy*dt;
      if(d.y>=SEA&&d.vy>0){d.live=false;d.el.setAttribute('visibility','hidden');
        if(Math.random()<0.22)splash(d.x);continue;}
      var sp2=Math.hypot(d.vx,d.vy)||1,L=Math.min(3,0.5+sp2*0.055);
      d.el.setAttribute('x1',d.x.toFixed(2));d.el.setAttribute('y1',d.y.toFixed(2));
      d.el.setAttribute('x2',(d.x-d.vx/sp2*L).toFixed(2));d.el.setAttribute('y2',(d.y-d.vy/sp2*L).toFixed(2));
      d.el.setAttribute('stroke-width',(0.55-0.2*(Math.abs(d.vy)<8?1:0)).toFixed(2)); // thinner at apex
      d.el.setAttribute('opacity',(Math.abs(d.vy)<8?0.38:0.6).toFixed(2));
    }
    for(var j3=0;j3<splashes.length;j3++){var s2=splashes[j3];if(!s2.live)continue;
      s2.age+=dt;var k3=s2.age/0.6;
      if(k3>=1){s2.live=false;s2.el.setAttribute('visibility','hidden');continue;}
      s2.el.setAttribute('rx',(0.4+3*k3).toFixed(2));
      s2.el.setAttribute('ry',(0.15+0.8*k3).toFixed(2));
      s2.el.setAttribute('stroke-width',(0.45*(1-k3)+0.1).toFixed(2));
      s2.el.setAttribute('opacity',(0.5*(1-k3)).toFixed(2));
    }
    handle.raf = requestAnimationFrame(frame);
  }
  handle.raf = requestAnimationFrame(frame);
  return handle;
};
